import { Link } from 'react-router-dom'
import { Check, ArrowRight, Sparkles } from 'lucide-react'
import { Modal } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import type { OnboardingStep, OnboardingProgress } from '@/utils/onboarding'

interface OnboardingWizardProps {
  isOpen: boolean
  steps: OnboardingStep[]
  progress: OnboardingProgress
  /** Skip for now — closes and won't auto-open again. */
  onSkip: () => void
  /** Finish — acknowledges the checklist and won't auto-open again. */
  onFinish: () => void
  /** Called when the user follows a step CTA (so the launcher can close). */
  onNavigate?: () => void
}

/**
 * First-login guided onboarding stepper (skippable). Shows the tier-aware
 * readiness checklist with a CTA per step; already-done steps are ticked. It
 * doesn't force navigation — following a CTA takes the user to the relevant
 * settings page and closes the wizard, and they can reopen it any time from the
 * account overview.
 */
export function OnboardingWizard({
  isOpen,
  steps,
  progress,
  onSkip,
  onFinish,
  onNavigate,
}: OnboardingWizardProps) {
  const allDone = progress.remaining === 0

  return (
    <Modal isOpen={isOpen} onClose={onSkip} title="Get your account ready" size="lg">
      <div className="space-y-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <p className="text-sm text-slate-600">
              A few quick things to help your account work its best. You can do
              these now or come back to them any time from your account overview.
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between text-xs font-medium text-slate-500 mb-1.5">
            <span>{progress.done} of {progress.total} done</span>
            <span>{progress.percent}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-primary-500 transition-all"
              style={{ width: `${progress.percent}%` }}
            />
          </div>
        </div>

        {/* Steps */}
        <ul className="space-y-2">
          {steps.map((step) => (
            <li
              key={step.id}
              className={`flex items-center gap-3 rounded-lg border p-3 ${
                step.done ? 'border-green-200 bg-green-50/50' : 'border-slate-200'
              }`}
            >
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                  step.done ? 'bg-green-500 text-white' : 'bg-slate-100 text-slate-400'
                }`}
                aria-hidden="true"
              >
                {step.done ? <Check className="w-3.5 h-3.5" /> : <span className="text-xs">•</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${step.done ? 'text-slate-500 line-through' : 'text-slate-800'}`}>
                  {step.title}
                  {step.required && !step.done && (
                    <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-amber-600">Recommended</span>
                  )}
                </p>
                {!step.done && (
                  <p className="text-xs text-slate-500 mt-0.5">{step.description}</p>
                )}
              </div>
              {!step.done && (
                <Link
                  to={step.ctaRoute}
                  onClick={onNavigate}
                  className="shrink-0 text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-0.5"
                >
                  {step.ctaLabel} <ArrowRight className="w-3 h-3" />
                </Link>
              )}
            </li>
          ))}
        </ul>

        {/* Actions */}
        <div className="flex items-center justify-between gap-3 pt-1">
          <button
            type="button"
            onClick={onSkip}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            {allDone ? 'Close' : 'Skip for now'}
          </button>
          <Button onClick={onFinish}>
            {allDone ? "I'm all set" : 'Done for now'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
